import { useCallback } from 'react';
import { useTheme } from 'styled-components';

import { FrStatuses, UserStatus } from '@libs/graphql';
import {
  LoanHealthStatus,
  LoanStatus,
  SwapStatusEnumKeys,
  TransactionStatusType,
  CryptoTransactionStatus,
  SwapSubStatuses,
} from '@libs/types';
import {
  BogTransactionStatusEnum,
  ExchangeLiquidatorStatusEnum,
  FrStatusEnum,
  KycDetailsStatusEnum,
  ManualActionStatusEnum,
  RiskLevelEnum,
  StakingHarvestModes,
  StakingStatuses,
  StatusEnum,
} from '@libs/backend-common';

type StatusType =
  | FrStatuses
  | UserStatus
  | TransactionStatusType
  | LoanHealthStatus
  | LoanStatus
  | StakingStatuses
  | StakingHarvestModes
  | SwapStatusEnumKeys
  | RiskLevelEnum
  | ManualActionStatusEnum
  | ExchangeLiquidatorStatusEnum
  | KycDetailsStatusEnum
  | BogTransactionStatusEnum
  | CryptoTransactionStatus
  | SwapSubStatuses;

export const useStatusInfo = () => {
  const { colors } = useTheme();

  const pendingColors = [colors.text.pending, colors.background.pending];
  const successColors = [colors.text.success, colors.background.success];
  const defaultColors = [
    colors.text.default,
    colors.surface.interactiveDefault,
  ];
  const criticalColors = [colors.text.critical, colors.background.critical];

  const statusColors = {
    [FrStatuses.UNVERIFIED]: defaultColors,
    [SwapStatusEnumKeys.DRAFT]: defaultColors,
    [SwapSubStatuses.DRAFT]: defaultColors,
    [StakingHarvestModes.END_OF_DURATION]: defaultColors,
    [StakingHarvestModes.IMMEDIATE]: defaultColors,
    [StakingHarvestModes.PER_ACCRUAL_INTERVAL]: defaultColors,
    [TransactionStatusType.DRAFT]: defaultColors,
    [CryptoTransactionStatus.SUBMITTED]: defaultColors,
    [ExchangeLiquidatorStatusEnum.INITIAL]: defaultColors,
    [KycDetailsStatusEnum.RESET]: defaultColors,

    [UserStatus.ACTIVE]: successColors,
    [FrStatuses.VERIFIED]: successColors,
    [LoanHealthStatus.HEALTHY]: successColors,
    [TransactionStatusType.SUCCEEDED]: successColors,
    [LoanStatus.COVERED]: successColors,
    [StakingStatuses.AWAITING_REDEMPTION]: successColors,
    [CryptoTransactionStatus.COMPLETED]: successColors,
    [SwapStatusEnumKeys.DEPOSIT_CONFIRMED]: successColors,
    [SwapStatusEnumKeys.EXCHANGE_COMPLETED]: successColors,
    [SwapStatusEnumKeys.WITHDRAW_COMPLETED]: successColors,
    [ManualActionStatusEnum.ACCEPTED]: successColors,
    [RiskLevelEnum.LOW]: successColors,
    [ExchangeLiquidatorStatusEnum.COMPLETED]: successColors,
    [KycDetailsStatusEnum.COMPLETED]: successColors,
    [BogTransactionStatusEnum.FINALIZED]: successColors,
    [CryptoTransactionStatus.SUBMITTED]: successColors,
    [CryptoTransactionStatus.COMPLETED]: successColors,

    [SwapSubStatuses.DEPOSIT_CONFIRMED]: successColors,
    [SwapSubStatuses.DEPOSIT_REBALANCING_CONFIRMED]: successColors,
    [SwapSubStatuses.EXCHANGE_COMPLETED]: successColors,
    [SwapSubStatuses.LIQUIDATION_CONFIRMED]: successColors,
    [SwapSubStatuses.WITHDRAW_COMPLETED]: successColors,
    [SwapSubStatuses.WITHDRAW_INTERNAL_REBALANCING_TRANSFER_CONFIRMED]:
      successColors,
    [SwapSubStatuses.WITHDRAW_REBALANCING_CONFIRMED]: successColors,

    [SwapSubStatuses.BLOCKED]: criticalColors,
    [SwapSubStatuses.DEPOSIT_FAILED]: criticalColors,
    [SwapSubStatuses.DEPOSIT_REBALANCING_FAILED]: criticalColors,
    [SwapSubStatuses.EXCHANGE_FAILED]: criticalColors,
    [SwapSubStatuses.LIQUIDATION_FAILED]: criticalColors,
    [SwapSubStatuses.WITHDRAW_FAILED]: criticalColors,
    [SwapSubStatuses.WITHDRAW_INTERNAL_REBALANCING_TRANSFER_FAILED]:
      criticalColors,
    [SwapSubStatuses.WITHDRAW_REBALANCING_FAILED]: criticalColors,

    [FrStatuses.TEMPORARY_REJECTED]: criticalColors,
    [LoanHealthStatus.CRITICAL]: criticalColors,
    [FrStatuses.REJECTED]: criticalColors,
    [UserStatus.BLOCKED]: criticalColors,
    [UserStatus.SUSPENDED]: criticalColors,
    [LoanStatus.LIQUIDATED]: criticalColors,
    [StakingStatuses.CLOSED]: criticalColors,
    [StakingStatuses.CLOSED_CONTINUED]: criticalColors,
    [StakingStatuses.CLOSED_EARLY_REDEEMED]: criticalColors,
    [CryptoTransactionStatus.FAILED]: criticalColors,
    [CryptoTransactionStatus.REJECTED]: criticalColors,
    [SwapStatusEnumKeys.DEPOSIT_FAILED]: criticalColors,
    [SwapStatusEnumKeys.EXCHANGE_FAILED]: criticalColors,
    [SwapStatusEnumKeys.WITHDRAW_FAILED]: criticalColors,
    [ManualActionStatusEnum.REJECTED]: criticalColors,
    [RiskLevelEnum.HIGH]: criticalColors,
    [ExchangeLiquidatorStatusEnum.FAILED]: criticalColors,
    [KycDetailsStatusEnum.REJECTED_DUPLICATE_APPLICANT]: criticalColors,
    [KycDetailsStatusEnum.REJECTED]: criticalColors,
    [BogTransactionStatusEnum.CONFLICTING]: criticalColors,
    [BogTransactionStatusEnum.INCOMPLETE]: criticalColors,
    [CryptoTransactionStatus.FAILED]: criticalColors,
    [CryptoTransactionStatus.REJECTED]: criticalColors,

    [FrStatuses.PENDING]: pendingColors,

    [SwapSubStatuses.DEPOSIT_PENDING]: pendingColors,
    [SwapSubStatuses.DEPOSIT_REBALANCING_PENDING]: pendingColors,
    [SwapSubStatuses.EXCHANGE_IN_PROGRESS]: pendingColors,
    [SwapSubStatuses.LIQUIDATION_PENDING]: pendingColors,
    [SwapSubStatuses.WITHDRAW_INTERNAL_REBALANCING_TRANSFER_PENDING]:
      pendingColors,
    [SwapSubStatuses.WITHDRAW_IN_PROGRESS]: pendingColors,
    [SwapSubStatuses.WITHDRAW_REBALANCING_PENDING]: pendingColors,

    [LoanHealthStatus.NEUTRAL]: pendingColors,
    [TransactionStatusType.PROCESSING]: pendingColors,
    [TransactionStatusType.PENDING_MANUAL_REVIEW]: pendingColors,
    [SwapStatusEnumKeys.WITHDRAW_IN_PROGRESS]: pendingColors,
    [SwapStatusEnumKeys.EXCHANGE_IN_PROGRESS]: pendingColors,
    [ManualActionStatusEnum.PENDING]: pendingColors,
    [SwapStatusEnumKeys.DEPOSIT_PENDING]: pendingColors,
    [RiskLevelEnum.MEDIUM]: pendingColors,
    [ExchangeLiquidatorStatusEnum.PARTIAL]: pendingColors,
    [KycDetailsStatusEnum.PENDING_DOCUMENT_UPLOAD]: pendingColors,
    [KycDetailsStatusEnum.PENDING_QUESTIONNAIRE]: pendingColors,
    [KycDetailsStatusEnum.PENDING_SELFIE_UPLOAD]: pendingColors,
    [BogTransactionStatusEnum.AWAITING_APPROVAL]: pendingColors,
  };

  const getStatusData = useCallback(
    (status?: StatusType) =>
      status
        ? {
            colors: statusColors[status],
            text: STATUS_LABELS[status],
          }
        : { colors: defaultColors, text: '' },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return {
    getStatusData,
  };
};

export const STATUS_LABELS = {
  [SwapSubStatuses.DRAFT]: 'status_draft',

  [UserStatus.ACTIVE]: 'status_active',
  [UserStatus.BLOCKED]: 'status_blocked',
  [UserStatus.SUSPENDED]: 'status_suspended',

  [StatusEnum.ACTIVE]: 'status_active',
  [StatusEnum.BLOCKED]: 'status_blocked',
  [StatusEnum.SUSPENDED]: 'status_suspended',

  [FrStatuses.UNVERIFIED]: 'status_unverified',
  [FrStatuses.VERIFIED]: 'status_verified',
  [FrStatuses.TEMPORARY_REJECTED]: 'status_temporary_rejected',
  [FrStatuses.REJECTED]: 'status_rejected',
  [FrStatuses.PENDING]: 'status_pending',
  [FrStatusEnum.UNVERIFIED]: 'status_unverified',
  [FrStatusEnum.VERIFIED]: 'status_verified',
  [FrStatusEnum.TEMPORARY_REJECTED]: 'status_temporary_rejected',
  [FrStatusEnum.REJECTED]: 'status_rejected',
  [FrStatusEnum.PENDING]: 'status_pending',

  [BogTransactionStatusEnum.AWAITING_APPROVAL]: 'status_awaiting_approval',
  [BogTransactionStatusEnum.CONFLICTING]: 'status_conflicting',
  [BogTransactionStatusEnum.FINALIZED]: 'status_finalized',
  [BogTransactionStatusEnum.INCOMPLETE]: 'status_incomplete',

  [LoanHealthStatus.HEALTHY]: 'status_healthy',
  [LoanHealthStatus.CRITICAL]: 'status_critical',
  [LoanHealthStatus.NEUTRAL]: 'status_neutral',

  [TransactionStatusType.DRAFT]: 'status_draft',
  [TransactionStatusType.SUCCEEDED]: 'status_succeeded',
  [TransactionStatusType.PROCESSING]: 'status_processing',
  [TransactionStatusType.PENDING_MANUAL_REVIEW]: 'status_pending_manual_review',

  [LoanStatus.COVERED]: 'status_covered',
  [LoanStatus.LIQUIDATED]: 'status_liquidated',

  [StakingStatuses.AWAITING_REDEMPTION]: 'status_awaiting_redemption',
  [StakingStatuses.CLOSED]: 'status_closed',
  [StakingStatuses.CLOSED_CONTINUED]: 'status_closed_continued',
  [StakingStatuses.CLOSED_EARLY_REDEEMED]: 'status_closed_early_redeemed',

  [StakingHarvestModes.END_OF_DURATION]: 'status_end_of_duration',
  [StakingHarvestModes.IMMEDIATE]: 'status_immediate',
  [StakingHarvestModes.PER_ACCRUAL_INTERVAL]: 'status_per_accrual_interval',

  [CryptoTransactionStatus.COMPLETED]: 'status_successful',
  [CryptoTransactionStatus.FAILED]: 'status_failed',
  [CryptoTransactionStatus.SUBMITTED]: 'status_submitted',

  [CryptoTransactionStatus.COMPLETED]: 'status_completed',
  [CryptoTransactionStatus.SUBMITTED]: 'status_submitted',
  [CryptoTransactionStatus.FAILED]: 'status_failed',
  [CryptoTransactionStatus.REJECTED]: 'status_rejected',

  [SwapStatusEnumKeys.DEPOSIT_CONFIRMED]: 'status_deposit_confirmed',
  [SwapStatusEnumKeys.DEPOSIT_FAILED]: 'status_deposit_failed',
  [SwapStatusEnumKeys.DEPOSIT_PENDING]: 'status_deposit_pending',
  [SwapStatusEnumKeys.DRAFT]: 'status_draft',
  [SwapStatusEnumKeys.EXCHANGE_COMPLETED]: 'status_exchange_completed',
  [SwapStatusEnumKeys.EXCHANGE_FAILED]: 'status_exchange_failed',
  [SwapStatusEnumKeys.EXCHANGE_IN_PROGRESS]: 'status_exchange_pending',
  [SwapStatusEnumKeys.WITHDRAW_COMPLETED]: 'status_withdraw_completed',
  [SwapStatusEnumKeys.WITHDRAW_FAILED]: 'status_withdraw_failed',
  [SwapStatusEnumKeys.WITHDRAW_IN_PROGRESS]: 'status_withdraw_pending',

  [RiskLevelEnum.LOW]: 'common_low',
  [RiskLevelEnum.MEDIUM]: 'common_medium',
  [RiskLevelEnum.HIGH]: 'common_high',

  [ManualActionStatusEnum.ACCEPTED]: 'common_approved',
  [ManualActionStatusEnum.PENDING]: 'status_pending',
  [ManualActionStatusEnum.REJECTED]: 'status_rejected',

  [ExchangeLiquidatorStatusEnum.COMPLETED]: 'exchange_status_completed',
  [ExchangeLiquidatorStatusEnum.FAILED]: 'exchange_status_failed',
  [ExchangeLiquidatorStatusEnum.PARTIAL]: 'exchange_status_partial',
  [ExchangeLiquidatorStatusEnum.INITIAL]: 'exchange_status_initial',

  [KycDetailsStatusEnum.COMPLETED]: 'status_completed',

  [KycDetailsStatusEnum.PENDING_DOCUMENT_UPLOAD]:
    'status_pending_document_upload',
  [KycDetailsStatusEnum.PENDING_QUESTIONNAIRE]: 'status_pending_questionnaire',
  [KycDetailsStatusEnum.PENDING_SELFIE_UPLOAD]: 'status_pending_selfie',
  [KycDetailsStatusEnum.REJECTED]: 'status_rejected',
  [KycDetailsStatusEnum.REJECTED_DUPLICATE_APPLICANT]: 'status_duplicate',
  [KycDetailsStatusEnum.RESET]: 'status_reset',

  [SwapSubStatuses.DEPOSIT_CONFIRMED]: 'status_deposit_confirmed',
  [SwapSubStatuses.DEPOSIT_REBALANCING_CONFIRMED]:
    'status_deposit_rebalancing_confirmed',
  [SwapSubStatuses.EXCHANGE_COMPLETED]: 'status_exchange_completed',
  [SwapSubStatuses.LIQUIDATION_CONFIRMED]: 'status_liquidation_confirmed',
  [SwapSubStatuses.WITHDRAW_COMPLETED]: 'status_withdraw_completed',
  [SwapSubStatuses.WITHDRAW_INTERNAL_REBALANCING_TRANSFER_CONFIRMED]:
    'status_withdraw_internal_rebalancing_confirmed',
  [SwapSubStatuses.WITHDRAW_REBALANCING_CONFIRMED]:
    'status_withdraw_rebalancing_confirmed',

  [SwapSubStatuses.BLOCKED]: 'status_blocked',
  [SwapSubStatuses.DEPOSIT_FAILED]: 'status_deposit_failed',
  [SwapSubStatuses.DEPOSIT_REBALANCING_FAILED]:
    'status_deposit_rebalancing_failed',
  [SwapSubStatuses.EXCHANGE_FAILED]: 'status_exchange_failed',
  [SwapSubStatuses.LIQUIDATION_FAILED]: 'status_liquidation_failed',
  [SwapSubStatuses.WITHDRAW_FAILED]: 'status_withdraw_failed',
  [SwapSubStatuses.WITHDRAW_INTERNAL_REBALANCING_TRANSFER_FAILED]:
    'status_withdraw_internal_rebalancing_failed',
  [SwapSubStatuses.WITHDRAW_REBALANCING_FAILED]:
    'status_withdraw_rebalancing_failed',

  [SwapSubStatuses.DEPOSIT_PENDING]: 'status_deposit_pending',
  [SwapSubStatuses.DEPOSIT_REBALANCING_PENDING]:
    'status_deposit_rebalancing_pending',
  [SwapSubStatuses.EXCHANGE_IN_PROGRESS]: 'status_exchange_pending',
  [SwapSubStatuses.LIQUIDATION_PENDING]: 'status_liquidation_pending',
  [SwapSubStatuses.WITHDRAW_INTERNAL_REBALANCING_TRANSFER_PENDING]:
    'status_withdraw_internal_rebalancing_pending',
  [SwapSubStatuses.WITHDRAW_REBALANCING_PENDING]:
    'status_withdraw_rebalancing_pending',
  [SwapSubStatuses.WITHDRAW_IN_PROGRESS]: 'status_withdraw_pending',
};
